import {
    AfterViewChecked,
    Component,
    EventEmitter,
    Input,
    Output,
    computed,
    input,
    Signal,
    inject,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TypeaheadSearchComponent, TypeaheadSearchResult } from '@r1-compass-library/components';
import { ComparatorMetadata } from '@r1-compass-library/models/content-library/content-library.model';
import { SalesHistorySelectionCriterionDataForm } from '@r1-compass-library/models/criteria/selection-criteria-form.model';
import { faCalendar, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DataMartCmt, DataMartField, DataMartPicklistValue } from '@r1-compass-library/models';
import { DivisionCriteriaSourceSettings } from '@r1-compass-library/models/division/division-criteria-source-settings.model';
import { DataMartService } from '@r1-compass-library/services';
import { DataMartConstants } from '@r1-compass-library/constants';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';

@Component({
    selector: 'r1-compass-app-sales-history-criterion',
    imports: [
        TypeaheadSearchComponent,
        FontAwesomeModule,
        FormsModule,
        MatCheckboxModule,
        MatChipsModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
    ],
    templateUrl: './sales-history-criterion.component.html',
    styleUrl: './sales-history-criterion.component.scss',
})
export class SalesHistoryCriterionComponent implements AfterViewChecked {
    readonly #datamartService = inject(DataMartService);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);

    @Input()
    criterionForm: FormGroup<SalesHistorySelectionCriterionDataForm> = new FormGroup(
        new SalesHistorySelectionCriterionDataForm()
    );

    readonly deletable = input<boolean>(true);

    readonly displayOnly = input<boolean>(false);

    readonly displayIndex = input<boolean>(true);

    required = input<boolean>(false);

    cmtLevels = input.required<DataMartPicklistValue[]>();

    selfManufacturedOptions = input.required<DataMartPicklistValue[]>();

    sspOptions = input.required<DataMartPicklistValue[]>();

    privateLabelOptions = input.required<DataMartPicklistValue[]>();

    channels = input.required<DataMartPicklistValue[]>();

    metrics = input.required<DataMartField[]>();

    index = input.required<number>();

    divisionCriteriaSourceSettings: Signal<DivisionCriteriaSourceSettings | undefined> = computed(
        () => this.#globalLoggedInUserStore.selectedDivisionConfig()?.config?.criteriaSourceSettings
    );

    tabClass = computed(() => (this.required() ? 'criterion-index-tab required-row-tab' : 'criterion-index-tab'));

    @Output()
    deleteCriterion: EventEmitter<number> = new EventEmitter<number>();

    ngAfterViewChecked() {
        if (this.displayOnly()) {
            this.criterionForm.disable();
        }
    }

    handpriceOptions: { value: string; displayValue: string }[] = [
        {
            value: 'Y',
            displayValue: 'Yes',
        },
        {
            value: 'N',
            displayValue: 'No',
        },
        {
            value: 'all',
            displayValue: 'All',
        },
    ];

    comparators: ComparatorMetadata[] = [
        { label: '=', value: 'equals', valueType: 'text' },
        { label: '>', value: 'greaterThan', valueType: 'text' },
        { label: '<', value: 'lessThan', valueType: 'text' },
        { label: '<=', value: 'lessThanOrEqualTo', valueType: 'text' },
        { label: '>=', value: 'greaterThanOrEqualTo', valueType: 'text' },
    ];

    categoryTypeaheadResults: DataMartCmt[] = [];

    vendorTypeaheadValueControl = new FormControl<string>('');
    vendorTypeaheadDisplayControl = new FormControl<string>('');
    vendorTypeaheadResults: TypeaheadSearchResult[] = [];

    faTrash = faTrash;
    faCalendar = faCalendar;

    type = 'Sales History';

    onRemoveSelectedVendor(index: number) {
        if (!this.displayOnly()) {
            const selectedVendors = this.criterionForm.controls['vendors'].value;
            selectedVendors.splice(index, 1);
            this.criterionForm.controls['vendors'].setValue([...selectedVendors]);
        }
    }

    onCmtLevelSelect() {
        this.categoryTypeaheadResults = [];
        this.criterionForm.controls.cmtCategory.reset();
    }

    onDelete() {
        this.deleteCriterion.emit(this.index());
    }

    onCategoryTypeaheadSearch(search: string) {
        const cmtLevel = Number(this.criterionForm.controls.cmtLevel.value);
        if (cmtLevel) {
            this.#datamartService.cmtSearch(cmtLevel, search).subscribe(cmtResponse => {
                this.categoryTypeaheadResults = cmtResponse.entries;
            });
        }
    }

    onVendorTypeaheadSearch(
        search: string,
        tableName: string = DataMartConstants.DefaultSalesHistoryVendorsTable,
        columnName: string = DataMartConstants.DefaultSalesHistoryVendorsColumn
    ) {
        if (this.divisionCriteriaSourceSettings()?.salesHistoryVendorsTable) {
            tableName = this.divisionCriteriaSourceSettings()!.salesHistoryVendorsTable!;
        }
        if (this.divisionCriteriaSourceSettings()?.salesHistoryVendorsColumn) {
            columnName = this.divisionCriteriaSourceSettings()!.salesHistoryVendorsColumn!;
        }
        this.#datamartService.typeaheadSearch(tableName, columnName, search).subscribe(results => {
            this.vendorTypeaheadResults = results.entries.map(entry => {
                return {
                    value: entry,
                    displayValue: entry,
                };
            });
        });
    }

    onVendorSelection(event: MatAutocompleteSelectedEvent) {
        const selectedVendors = this.criterionForm.controls.vendors.value;
        this.criterionForm.controls.vendors.setValue([...selectedVendors, event.option.value.value]);
        this.vendorTypeaheadDisplayControl.setValue('');
        this.vendorTypeaheadValueControl.setValue('');
        this.vendorTypeaheadResults = [];
    }
}
